import { useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  User,
} from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import { collection, doc, getDoc, getDocs, limit, query } from "firebase/firestore";
import { db } from "../firebase";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u && u.email) {
        try {
          const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Firestore timeout")), 10_000)
          );
          const countSnap = await Promise.race([
            getDocs(query(collection(db, "allowedUsers"), limit(1))),
            timeout,
          ]);
          if (countSnap.empty) {
            // No allowlist configured — allow everyone
            setAuthorized(true);
          } else {
            const userDoc = await getDoc(doc(db, "allowedUsers", u.email));
            setAuthorized(userDoc.exists());
          }
        } catch (err) {
          console.error("Auth check failed:", err);
          setAuthorized(false);
        }
      } else {
        setAuthorized(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = () => signInWithPopup(auth, googleProvider);
  const loginWithEmail = (email: string, password: string) =>
    signInWithEmailAndPassword(auth, email, password);
  const registerWithEmail = (email: string, password: string) =>
    createUserWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);
  const resetPassword = (email: string) => sendPasswordResetEmail(auth, email);

  return { user, loading, authorized, login, loginWithEmail, registerWithEmail, logout, resetPassword };
}
